import { useInventory, useUpdateInventoryFinishedStatus } from '@gozoki/api';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    ListItemText,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';

import { InventorySummary } from '@gozoki/api-types';
import { frenchFormat } from '@gozoki/tools';
import { useQueryClient } from '@tanstack/react-query';
import BlockerLoader from '../../components/loaders/BlockerLoader';
import { useInventoryProductRows } from '../../utils/hooks/useInventoryProductRows';
import { fonts } from '../../utils/theme/fonts.theme';

interface InventoryStateDetailsProps {
    pendingInventory: InventorySummary;
    shopId: number;
}

const MAX_DELTA = 4; // 5 units

export const InventoryStateDetails = ({ pendingInventory, shopId }: InventoryStateDetailsProps) => {
    const queryClient = useQueryClient();

    const [showWarningDialog, setShowWarningDialog] = useState<boolean>(false);

    const [problematicProducts, setProblematicProducts] = useState<string[]>([]); // labels of problematic products

    const { mutate: updateInventoryFinishedStatus, isPending } = useUpdateInventoryFinishedStatus();

    const { data: inventoryDetail, isPending: isLoadingInventoryDetail } = useInventory(
        pendingInventory?.id ?? 0
    );

    const productRows = useInventoryProductRows(inventoryDetail);

    const handleFinishInventory = useCallback(() => {
        if (!inventoryDetail) return null;
        updateInventoryFinishedStatus(
            {
                id: inventoryDetail.id,
                isFinished: true,
            },
            {
                onSuccess: () => {
                    setProblematicProducts([]);
                    setShowWarningDialog(false);
                    // Invalidate the detail query
                    queryClient.invalidateQueries({
                        queryKey: ['inventory', inventoryDetail.id],
                    });
                    // Invalidate the store's inventory list
                    queryClient.invalidateQueries({
                        queryKey: ['storeInventories', shopId],
                    });
                },
            }
        );
        return null;
    }, [inventoryDetail, updateInventoryFinishedStatus, queryClient, shopId]);

    const handleCheckAndFinishInventory = useCallback(() => {
        if (!inventoryDetail) return null;
        // Check for products with high delta
        const productsWithHighDelta = productRows.filter((row) => Math.abs(row.delta) > MAX_DELTA);

        if (productsWithHighDelta.length > 0) {
            const problematicProductsLabels = productsWithHighDelta.map((p) => p.label);

            setProblematicProducts(problematicProductsLabels);
            setShowWarningDialog(true);
        } else {
            handleFinishInventory();
        }

        return null;
    }, [inventoryDetail, productRows, handleFinishInventory]);

    const isFinished = useMemo(() => inventoryDetail?.isFinished, [inventoryDetail?.isFinished]);

    if (!inventoryDetail) return null;

    return (
        <>
            <BlockerLoader isLoading={isLoadingInventoryDetail || isPending} />
            <Paper sx={{ p: 2 }}>
                <Stack gap={2}>
                    <Stack flexDirection="column">
                        <Typography style={fonts.pageSubtitle} flexGrow={1}>
                            {isFinished ? 'Inventaire terminé' : 'Inventaire en cours'}
                        </Typography>
                        <Typography color="text.secondary" style={fonts.tabTitle} flexGrow={1}>
                            {isFinished
                                ? 'Tous les articles en boutique ont été scannés'
                                : "Scanner tous les articles en boutique puis marquer l'inventaire comme terminé"}
                        </Typography>
                        <Typography
                            display="flex"
                            alignItems="center"
                            style={{ ...fonts.stockCardTitle, marginTop: '12px' }}
                        >
                            {isFinished
                                ? 'Articles scannés :'
                                : 'Articles scannés (inventaire en cours) :'}
                            <span style={{ ...fonts.bigNumberOrange, marginLeft: '4px' }}>
                                {frenchFormat(inventoryDetail?.incomingStock.length)}
                            </span>
                        </Typography>
                        {/* set the button at the end of the line */}
                        {!isFinished && (
                            <Stack direction="row" justifyContent="flex-end">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleCheckAndFinishInventory}
                                >
                                    Marquer l'inventaire comme terminé
                                </Button>
                            </Stack>
                        )}
                    </Stack>
                </Stack>
            </Paper>

            <Dialog
                open={showWarningDialog}
                onClose={() => setShowWarningDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {problematicProducts.length === 1
                        ? 'Écart important détecté'
                        : 'Écarts importants détectés'}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" paragraph>
                        {problematicProducts.length === 1
                            ? 'Un produit présente'
                            : `${problematicProducts.length} produits présentent`}{' '}
                        un écart supérieur à {MAX_DELTA} unités entre le stock théorique et le stock
                        relevé.
                    </Typography>
                    <List dense disablePadding>
                        {problematicProducts.map((product) => (
                            <ListItem key={product} disablePadding>
                                <ListItemText
                                    primary={`• ${product}`}
                                    primaryTypographyProps={{ fontSize: '0.75rem' }}
                                />
                            </ListItem>
                        ))}
                    </List>
                    <Typography
                        style={{ marginTop: '20px' }}
                        variant="body2"
                        color="text.secondary"
                    >
                        Vérifiez bien que tous les produits présents en boutique ont été scannés et
                        que la livraison du jour est bien prise en compte.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowWarningDialog(false)} color="error">
                        Annuler
                    </Button>
                    <Button onClick={handleFinishInventory} color="primary">
                        Terminer quand même
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
